import { Input, Table, message } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { getSuppliersBySearch } from "api/coreapi/suppliers";
import CustomerStatusSelect from "components/customerStatusSelect";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetCustomersBySearchPayload } from "shared/models/payloads/getCustomersBySearchPayload";
import { SupplierOverview } from "shared/models/supplierOverviewModel";
import { formatDate } from "utils/dateUtils";

const { Search } = Input;

const SupplierList = () => {
  const [data, setData] = useState<SupplierOverview[]>();
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  let timeoutId: NodeJS.Timeout;
  const [filters, setFilters] = useState<GetCustomersBySearchPayload>({
    text: "",
    statusIds: [],
    orderByColumns: [],
    orderByDirection: 0,
    pageNumber: pagination.current,
    pageSize: pagination.pageSize
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getSuppliersBySearch({ ...filters, pageSize: pagination.pageSize, pageNumber: pagination.current });
        setData(result.result.data);
        setPagination({ ...pagination, total: result.result.totalPages });
      } catch (error) {
        message.error('Error obteniendo información de proveedores.');

      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [filters, pagination.pageSize, pagination.current]);

  const handleTableChange = (
    pagination: any,
    tableFilters: any,
    sorter: SorterResult<SupplierOverview> | SorterResult<SupplierOverview>[]) => {
    let orderByColumns: string[] = [];
    let orderByDirection: 0 | 1 = 0;

    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize
    });

    if (Array.isArray(sorter)) {
      orderByColumns = sorter.map((s) => s.columnKey ? s.columnKey.toString() : '');
      orderByDirection = sorter[0].order === 'descend' ? 1 : 0;
    } else if (sorter?.columnKey) {
      orderByColumns = [sorter.columnKey.toString()];
      orderByDirection = sorter.order === 'descend' ? 1 : 0;
    }

    setFilters({
      ...filters,
      orderByColumns: orderByColumns,
      orderByDirection: orderByDirection,
    });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, current: 1 });
  };

  const debounceHandleFilterChange = (key: string, value: any) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => handleFilterChange(key, value), 1000);
  };

  const columns = [
    {
      title: "# Identificación",
      dataIndex: "identificationNumber",
      key: "identificationNumber",
    },
    {
        title: "Tipo",
        dataIndex: "supplierTypeName",
        key: "supplierTypeName",
        sorter: (a: SupplierOverview, b: SupplierOverview) => a.supplierTypeName.localeCompare(b.supplierTypeName),
      },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      sorter: (a: SupplierOverview, b: SupplierOverview) => a.name.localeCompare(b.name),
    },
    {
      title: "Estado",
      dataIndex: "supplierStatusName",
      key: "supplierStatusName",
      sorter: (a: SupplierOverview, b: SupplierOverview) => a.supplierStatusName.localeCompare(b.supplierStatusName),
    },
    {
      title: "Creado el",
      dataIndex: "createdOn",
      key: "createdOn",
      sorter: (a: SupplierOverview, b: SupplierOverview) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
      render: (text: Date, record: SupplierOverview) => formatDate(record.createdOn, 'DateAndTime'),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Buscar por nombre o identificación"
          onChange={(e) => debounceHandleFilterChange("text", e.target.value)}
          style={{ width: 300, marginRight: 16 }}
        />
        <CustomerStatusSelect 
          mode="multiple" 
          onChange={(value) => handleFilterChange("statusIds", value)}
          placeholder="Filtrar por estado"
          style={{ width: 200, marginRight: 16 }} />
      </div>
      <Table
        rowKey='id'
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => navigate(`/suppliers/${record.id}`)
        })}
      />
    </>
  );
};

export default SupplierList;
